<template>
    <div :class="['uik-avatar__wrapper', (size ? 'uik-avatar__' + size: ''), (textOnly ? 'uik-avatar__textOnly':'')]">
        <div v-if="!textOnly" class="uik-avatar__avatarWrapper">
            <div
                v-if="imageArray.length > 1"
                :class="['uik-avatar__avatarMultiWrapper', 'uik-avatar__s' + imageArray.length]"
            >
                <img
                    v-for="(item, index) in imageArray"
                    :key="index"
                    class="uik-avatar__avatar"
                    :src="item"
                    alt
                >
            </div>
            <img
                v-if="showImage"
                :src="imageUrl"
                class="uik-avatar__avatar"
                alt="Profile"
                @error="showImage = false">
            <div
                v-if="!showImage && imageArray.length <= 1"
                :class="['uik-avatar__avatarPlaceholder', (color ? color:'')]"
            >{{ initials ? initials : name.split(" ").map((n)=>n[0]).join("").substring(0, 2) }}</div>
        </div>
        <div v-if="name && !imageOnly" class="uik-avatar__info">
            <div v-if="textTop" class="uik-avatar__textTop">{{ textTop }}</div>
            <div class="uik-avatar__name">{{ name }}</div>
            <div v-if="textBottom" class="uik-avatar__textBottom">{{ textBottom }}</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "UikAvatar",
    props: {
        imageArray: {
            type: Array,
            default: () => []
        },
        imageUrl: { type: String, default: "" },
        name: { type: String, default: "" },
        initials: { type: String, default: "" },
        textTop: { type: String, default: "" },
        textBottom: { type: String, default: "" },
        color: { type: String, default: "" },
        actionIcon: { type: String, default: "" },
        imageOnly: { type: Boolean, default: false },
        textOnly: { type: Boolean, default: false },
        size: {
            type: String,
            default: "",
            validator: value => {
                let options = [
                    "",
                    "small",
                    "large",
                    "larger",
                    "extraLarge",
                    "jumbo"
                ]
                return options.includes(value)
            }
        }
    },
    data () {
        return {
            showImage: false
        }
    },
    watch: {
        imageUrl () {
            if (this.imageUrl) {
                this.showImage = true
            } else {
                this.showImage = false
            }
        }
    },
    created () {
        if (this.imageUrl) this.showImage = true
    }
}
</script>

<style lang="scss">
.uik-avatar__info {
    margin-left: 12px;
    flex-grow: 1;
    & > * {
        display: block;
    }
}

.uik-avatar__textOnly {
    .uik-avatar__info {
        margin-left: 0 !important;
    }
}

.uik-avatar__wrapper {
    display: flex;
    align-items: center;
    max-width: 100%;
    & > * {
        min-width: 1px;
    }
    .uik-avatar__name {
        /* Author: */
        font-size: 0.875rem;
        color: $textMain;
        line-height: 1.375rem;
        overflow: hidden;
        white-space: nowrap;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        i {
            font-style: normal;
            color: $textSecondary;
        }
    }
    &.uik-avatar__highlighted {
        .uik-avatar__name {
            font-weight: 500;
        }
    }
    .uik-avatar__avatarWrapper {
        position: relative;
        height: 38px;
        width: 38px;
        flex-shrink: 0;
        .uik-avatar__avatarAction {
            position: absolute;
            bottom: 0;
            right: 0;
        }
        .uik-avatar__avatarPlaceholder {
            height: 100%;
            width: 100%;
            border-radius: 50%;
            color: $textSecondary;
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: 500;
            line-height: 1.5rem;
            text-transform: uppercase;
            background-color: transparent;
            border: $borderDefault; // generating
            @each $name, $color in $colors {
                &:global(.#{"" + $name}) {
                    color: white;
                    background-color: $color;
                    border-color: transparent;
                }
            } // extra
            &:global(.lightblue) {
                background-color: #98caec;
                background-image: linear-gradient(
                    -45deg,
                    #98caec 0%,
                    #44ade5 100%
                );
                border-color: transparent;
                color: white;
            }
        }
        .uik-avatar__avatar {
            height: 100%;
            width: 100%;
            border-radius: 50%;
            display: block;
            object-fit: cover;
            & + * {
                margin-left: 0.75rem;
            }
        }
        .uik-avatar__avatarMultiWrapper {
            height: 100%;
            width: 100%;
            border-radius: 50%;
            overflow: hidden;
            display: flex;
            & > .uik-avatar__avatar {
                border-radius: 0;
                margin: 0 !important;
            }
            &.uik-avatar__s2 > .uik-avatar__avatar {
                transform: translateX(-50%);
            }
            &.uik-avatar__s3 > .uik-avatar__avatar {
                &:nth-child(1) {
                    transform: translateX(-50%);
                }
                &:nth-child(2),
                &:nth-child(3) {
                    height: 50%;
                    width: 50%;
                }
                &:nth-child(2) {
                    transform: translateX(-100%);
                }
                &:nth-child(3) {
                    transform: translateX(-200%) translateY(100%);
                }
            }
            &.uik-avatar__s4 {
                flex-wrap: wrap;
                & > .uik-avatar__avatar {
                    height: 50%;
                    width: 50%;
                }
            }
        }
    }
    .uik-avatar__textTop {
        font-size: 0.75rem;
        color: $textSecondary;
        line-height: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
    }
    .uik-avatar__textBottom {
        font-size: 0.75rem;
        color: $textSecondary;
        line-height: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
    }
    .uik-avatar__textTop,
    .uik-avatar__textBottom,
    .uik-avatar__name {
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &.uik-avatar__small {
        .uik-avatar__avatarWrapper {
            font-size: 12px;
            height: 28px;
            width: 28px;
        }
    }
    &.uik-avatar__large,
    &.uik-avatar__larger,
    &.uik-avatar__jumbo,
    &.uik-avatar__extraLarge {
        .uik-avatar__avatarWrapper {
            font-size: 18px;
            height: 60px;
            width: 60px;
        }
        .uik-avatar__textTop,
        .uik-avatar__textBottom {
            font-size: 0.875rem;
            line-height: 1.375rem;
            color: $textBase;
        }
        .uik-avatar__name {
            font-size: 1rem;
            line-height: 1.5rem;
        }
    }
    &.uik-avatar__larger {
        .uik-avatar__textTop,
        .uik-avatar__textBottom {
            color: $textBase;
        }
        .uik-avatar__avatarWrapper {
            font-size: 26px;
            height: 80px;
            width: 80px;
        }
    }
    &.uik-avatar__extraLarge {
        .uik-avatar__avatarWrapper {
            font-size: 36px;
            height: 100px;
            width: 100px;
        }
    }
    &.uik-avatar__jumbo {
        .uik-avatar__name {
            font-size: 1.25rem;
            line-height: 1.5;
        }
        .uik-avatar__avatarWrapper {
            font-size: 36px;
            height: 120px;
            width: 120px;
        }
        .uik-avatar__info {
            margin-left: 24px;
        }
    }
}

.uik-avatar__statusWrapper {
    margin-left: 10px;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    color: $textSecondary;
    font-size: 0.75rem;
    .uik-avatar__statusOnline {
        background: $success;
        height: 6px;
        width: 6px;
        border-radius: 50%;
    }
}
</style>
